import React, { useState, useMemo } from 'react';
import globalResilienceData from '../../data/globalResilienceData.json';
import IconSort from '../common/icon/icon-sort';
import ReverseIconSort from '../common/icon/reverse-icon-sort';
import IconFilter from '../common/icon/icon-filter';
import IconClose from '../common/icon/icon-close';
import IconArrowSelect from "../common/icon/icon-arrow-select";
import ResetButton from '../common/resetButton/ResetButton';
import CountryRow from './CountryRow';
import styles from './GlobalResilienceIndex.module.scss';

const GlobalResilienceIndex = () => {
  const [activeFilter, setActiveFilter] = useState(null); // Single filter at a time
  const [confidenceFilter, setConfidenceFilter] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [showAllMobile, setShowAllMobile] = useState(false);

  // Single filter options
  const filterOptions = [
    { type: 'score', label: 'Score', values: ['Total Global Investment Risk and Resilience Index Score'] },
    { type: 'region', label: 'Region', values: ['Africa', 'Americas', 'Asia', 'Caribbean', 'Europe', 'Middle East', 'Oceania'] },
    { type: 'incomeGroup', label: <>Income Group<br />(Gross National Income)</>, values: ['High', 'Upper Middle', 'Lower Middle', 'Low'] },
    { type: 'gdpGrowth', label: <>GDP Growth<br />(5-Year Average)</>, values: ['High', 'Moderate', 'Low'] },
    { type: 'confidenceScore', label: 'Research Dataset', values: ['≥85% Confidence Score'] }
  ];

  // Filter and sort countries
  const filteredData = useMemo(() => {
    let filtered = [...globalResilienceData];

    // Apply single active filter
    if (activeFilter) {
      if (activeFilter.type === 'confidenceScore') {
        filtered = filtered.filter(country => (country.confidence || 0) >= 85);
      } else {
        filtered = filtered.filter(country => {
          const countryValue = (country[activeFilter.type] || '').toLowerCase();
          const filterValue = (activeFilter.value || '').toLowerCase();
          return countryValue === filterValue;
        });
      }
    }

    // Apply confidence filter (affects sorting when active)
    if (confidenceFilter) {
      filtered = filtered.filter(country => (country.confidence || 0) >= 85);
    }

    // Add rank based on filtered data
    filtered.forEach((country, index) => {
      country.displayRank = index + 1;
    });

    return filtered;
  }, [activeFilter, confidenceFilter]);

  const handleFilterSelect = (type, value) => {
    if (type === 'confidenceScore') {
      setConfidenceFilter(!confidenceFilter);
      setActiveFilter(null);
    } else if (type === 'score') {
      // Reset to show all scores and sort by totalScore descending
      setActiveFilter(null);
      setConfidenceFilter(false);
      setSortConfig({ key: 'totalScore', direction: 'desc' });
    } else {
      // Reset confidence filter when selecting other filters
      setConfidenceFilter(false);
      // Toggle filter selection
      if (activeFilter && activeFilter.type === type && activeFilter.value === value) {
        setActiveFilter(null); // Deselect if same filter clicked
      } else {
        setActiveFilter({ type, value });
      }
    }
    // Close the dropdown after selection
    setShowFilterMenu(false);
  };

  const resetFilters = () => {
    setActiveFilter(null);
    setConfidenceFilter(false);
    setShowFilterMenu(false);
  };

  const getActiveFilterLabel = () => {
    if (confidenceFilter) return '≥85% Confidence Score';
    if (activeFilter) {
      if (activeFilter.type === 'score') {
        return 'Total Global Investment Risk and Resilience Index Score (Out of 100)';
      }
      const filterGroup = filterOptions.find(group => group.type === activeFilter.type);
      return `${filterGroup ? filterGroup.label : 'Filter'}: ${activeFilter.value}`;
    }
    return 'Total Global Investment Risk and Resilience Index Score (Out of 100)';
  };

  const getColumnHeader = () => {
    if (confidenceFilter) return '≥85% Confidence Score';
    if (activeFilter) {
      // Don't show anything for score type (Total R&R)
      if (activeFilter.type === 'score') return '';
      const filterGroup = filterOptions.find(group => group.type === activeFilter.type);
      return filterGroup ? filterGroup.label : '';
    }
    return '';
  };

  const getColumnValue = (country) => {
    if (confidenceFilter) return `${country.confidence || 0}`;
    if (activeFilter) {
      if (activeFilter.type === 'region') return country.region;
      if (activeFilter.type === 'incomeGroup') return country.incomeGroup;
      if (activeFilter.type === 'gdpGrowth') return country.gdpGrowth;
    }
    // Keep empty when no filter is active
    return '';
  };

  const toggleRowExpansion = (countryCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(countryCode)) {
      newExpanded.delete(countryCode);
    } else {
      newExpanded.add(countryCode);
    }
    setExpandedRows(newExpanded);
  };

  const toggleSectionExpansion = (sectionId) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const [sortConfig, setSortConfig] = useState({ key: 'totalScore', direction: 'desc' });

  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Primary sort by the selected column
      if (aVal !== bVal) {
        const primaryComparison = aVal > bVal ? 1 : -1;
        return sortConfig.direction === 'asc' ? primaryComparison : -primaryComparison;
      }

      // If values are equal, use globalRank as tiebreaker
      // For descending: lower rank (13) comes before higher rank (14)
      // For ascending: higher rank (14) comes before lower rank (13)
      const rankComparison = (a.globalRank || 999) - (b.globalRank || 999);
      return sortConfig.direction === 'asc' ? -rankComparison : rankComparison;
    });

    // Update ranks after sorting
    sorted.forEach((country, index) => {
      country.displayRank = index + 1;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Get display data (limit to 20 on mobile if not showing all)
  const displayData = useMemo(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 799 && !showAllMobile) {
      return sortedData.slice(0, 20);
    }
    return sortedData;
  }, [sortedData, showAllMobile]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <ReverseIconSort /> : <IconSort />;
    }
    return <IconSort />;
  };


  return (
    <div className={styles.globalResilienceIndex}>
      {/* Mobile Filter Dropdown */}
      <div className={styles.mobileFilterContainer}>
        <div className={styles.filterDropdown}>
          <button
            className={styles.filterButton}
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            {getActiveFilterLabel()}
              <IconArrowSelect />
          </button>
          {showFilterMenu && (
            <div className={styles.filterMenu}>
              <div className={styles.filterMenuHeader}>
                <ResetButton onClick={resetFilters} />
                <button onClick={() => setShowFilterMenu(false)} className={styles.closeButton}>
                  <IconClose />
                </button>
              </div>

              {filterOptions.map(filterGroup => (
                <div key={filterGroup.type} className={styles.filterGroup}>
                  <div className={styles.filterGroupLabel}>{filterGroup.label}</div>
                  {filterGroup.values.map(value => (
                    <label key={value} className={styles.filterOption}>
                      <input
                        type={filterGroup.type === 'confidenceScore' ? 'checkbox' : 'radio'}
                        name="filter"
                        checked={
                          filterGroup.type === 'confidenceScore'
                            ? confidenceFilter
                            : activeFilter?.type === filterGroup.type && activeFilter?.value === value
                        }
                        onChange={() => handleFilterSelect(filterGroup.type, value)}
                      />
                      <div>
                        {value}
                        {filterGroup.type === 'confidenceScore' && (
                          <div className={styles.filterSubtext}>(% of Data Available)</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.resilienceTable}>
          <thead>
            <tr>
              <th className={styles.rankHeader}>Rank</th>
              <th
                className={`${styles.countryHeader} ${styles.sortable} ${sortConfig.key === 'country' ? styles.selectedColumn : ''}`}
                onClick={() => handleSort('country')}
              >
                <div className={styles.sortColumnTitle}>
                  <span>Country/Territory</span>
                  {renderSortArrow('country')}
                </div>
              </th>
              <th
                className={`${styles.scoreHeader} ${styles.sortable} ${sortConfig.key === 'totalScore' ? styles.selectedColumn : ''} ${(activeFilter && activeFilter.type !== 'score') || confidenceFilter ? styles.hideOnFilter : ''}`}
                onClick={() => handleSort('totalScore')}
              >
                <div className={styles.sortColumnTitle}>
                  <span>
                    Total Global Investment<br/>
                    Risk and Resilience<br/>
                    Index Score (Out of 100)
                  </span>
                  {renderSortArrow('totalScore')}
                </div>
              </th>
              <th className={styles.expandHeader}></th>
              <th className={`${styles.filterHeader} ${(activeFilter && activeFilter.type !== 'score') || confidenceFilter ? styles.showOnFilter : ''}`}>
                  <div className={`d-md-none`}>
                    {getColumnHeader() || 'Filter'}
                  </div>
                  <div className={`${styles.filterDropdown} d-none d-md-block`}>
                    <button
                      className={styles.filterButton}
                      onClick={() => setShowFilterMenu(!showFilterMenu)}
                    >
                      {getColumnHeader() || 'Filter'}
                       <IconFilter />
                    </button>
                    {showFilterMenu && (
                      <div className={styles.filterMenu}>
                        <div className={styles.filterMenuHeader}>
                          <ResetButton onClick={resetFilters} />
                          <button onClick={() => setShowFilterMenu(false)} className={styles.closeButton}>
                            <IconClose />
                          </button>
                        </div>

                        {filterOptions.filter(group => group.type !== 'score').map(filterGroup => (
                          <div key={filterGroup.type} className={styles.filterGroup}>
                            <div className={styles.filterGroupLabel}>{filterGroup.label}</div>
                            {filterGroup.values.map(value => (
                              <label key={value} className={styles.filterOption}>
                                <input
                                  type={filterGroup.type === 'confidenceScore' ? 'checkbox' : 'radio'}
                                  name="filter"
                                  checked={
                                    filterGroup.type === 'confidenceScore'
                                      ? confidenceFilter
                                      : activeFilter?.type === filterGroup.type && activeFilter?.value === value
                                  }
                                  onChange={() => handleFilterSelect(filterGroup.type, value)}
                                />
                                <div>
                                  {value}
                                  {filterGroup.type === 'confidenceScore' && (
                                    <div className={styles.filterSubtext}>(% of Data Available)</div>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

              </th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((country) => (
              <CountryRow
                key={country.code}
                country={country}
                sortConfig={sortConfig}
                getColumnValue={getColumnValue}
                expandedRows={expandedRows}
                toggleRowExpansion={toggleRowExpansion}
                expandedSections={expandedSections}
                toggleSectionExpansion={toggleSectionExpansion}
                hasActiveFilter={!!(activeFilter || confidenceFilter)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Load All Button (Mobile Only) */}
      {!showAllMobile && sortedData.length > 20 && (
        <div className={styles.loadAllContainer}>
          <button
            className={styles.loadAllButton}
            onClick={() => setShowAllMobile(true)}
          >
            Load All ({sortedData.length} countries)
          </button>
        </div>
      )}
    </div>
  );
};

export default GlobalResilienceIndex;